import { AuthFacade } from './../../store/auth/facade/auth.facade';
import { catchError, finalize, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../http/auth.service';
import { throwError as observableThrowError, BehaviorSubject, of } from 'rxjs';
import { Injectable, OnDestroy } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse } from '@angular/common/http';
import { IJwtoken } from 'src/app/models/jwtoken';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    newToken: string;
    refreshToken = '';

    constructor(private authService: AuthService, private authFacade: AuthFacade) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): any {
        return next.handle(req).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse) {
                    if (error.status === 400 && error.error?.error_description !== 'invalid token') {
                        return this.handle400Error(error);
                    }
                    if (error.status === 401 || error.error?.error_description === 'invalid token') {
                        return this.handle401Error(req, next);
                    }
                }
                return observableThrowError(error);
            })
        );
    }

    updateToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
      if (!Array.isArray(req.body) && req.body?.map?.has('token')) {
          // For authentication type requests, the auth token is in the body, rather than the header,
          // and the payload parameters are contained in a Map object. Update this if present, otherwise replace the headers.
          req.body.map.set('token', [token]);
          return req;
      }

      return req.clone({ setHeaders: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } });
    }

    handle400Error(error) {
        if (error && (error.status === 400) &&
            error.error && (error.error.error === 'invalid_grant') &&
            !error.error.error_description.includes("The user")) {
            // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
            return this.logout();
        }
        return observableThrowError(error);
    }

    handle401Error(req: HttpRequest<any>, next: HttpHandler): any {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.tokenSubject.next(null);

            return this.authService.refreshToken()
                .pipe(
                    catchError(err => {
                        // Place the catch function here so it only applies to the refresh call, else we will be logged out
                        // if the request retry errors out. Rethrow error so we only reattempt request if the refresh succeeds.
                        this.logout();
                        
                        throw err;
                    }),
                    switchMap((result: IJwtoken) => {
                        if (result) {
                            this.tokenSubject.next(result.access_token);
                            return next.handle(this.updateToken(req, result.access_token));
                        }
                        // Refresh call succeeded but returned empty token
                        this.logout();
                        
                        return of('logout');
                    }),
                    finalize(() => {
                        this.isRefreshingToken = false;
                    })
                );
        } else {
            return this.tokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => {
                    return next.handle(this.updateToken(req, token));
                }));
        }
    }
    
    private logout(){
        this.authFacade.logout();
        
        // If multiple requests come through this interceptor at once, only one of them triggers the refresh token call.
        // The rest of them get buffered up against the tokensubject, where if the refresh token call exceeds a value to
        // emitted to the tokensubject to trigger the other requests to be reattempted. An issue we had was that if that
        // refresh token call fails, then the user is logged out - but those other requests are still buffered up against
        // token tokensubject. This means once the user logs back in again, those requests are retriggered with the new token.
        
        // This didn't cause any visible issue (No incorrect data would be represented), but if the user logged in with
        // different permissions than they did when those requests were originally triggered, then the reattempts would 
        // return with 403's, which would then cause an error toastr to appear for no apparent reason (To the user).
        
        // To fix this, we complete the tokenSubject and reinitialize it to ensure that any buffered requests are cleared
        // from it.
        this.tokenSubject.complete();
        this.tokenSubject = new BehaviorSubject<string>(null);
    }
}
